import React, { useState, useEffect } from "react";
import { Range, getTrackBackground } from "react-range";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../../common/hooks/hooks";
import { setPriceRange } from "../../../productSlice";
import { RootState } from "../../../../../../app/store";
import { useGetProductsCollectionDataQuery } from "../../../api/productApi";
import { selectCurrencySymbol } from "../../../productSelectors";

const PriceFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t, ready: areTranslationsReady } = useTranslation();

  // --- Redux State ---
  const filters = useAppSelector((state: RootState) => state.product.filters);
  const { minPrice: reduxMinPrice, maxPrice: reduxMaxPrice } = filters;

  // Use the new selector to get the currency symbol from the API state
  const currencySymbol = useAppSelector(selectCurrencySymbol);

  // --- Derived State (Single Source of Truth) ---
  const isDirty = reduxMinPrice !== null || reduxMaxPrice !== null;

  // --- Constants ---
  const ABSOLUTE_MIN = 0;
  const STEP = 10;
  // currencySymbol is now from the Redux selector

  // --- Data Fetching for Price Range ---
  // This query also provides the currency symbol and max price.
  const { data: collectionData, isLoading: isCollectionLoading } =
    useGetProductsCollectionDataQuery({
      category: filters.category,
      tag: filters.tag,
      brand: filters.brand,
      search: filters.search,
    });

  const ABSOLUTE_MAX = collectionData?.maxPrice ?? 1000;

  // --- Local UI State for Slider Interaction ---
  const [values, setValues] = useState<[number, number]>([
    ABSOLUTE_MIN,
    ABSOLUTE_MAX,
  ]);

  // --- Synchronization Effect with Edge Case Handling ---
  useEffect(() => {
    if (isCollectionLoading) return;

    // --- EDGE CASE HANDLER ---
    // If a price filter was active BUT the user's previous minimum is now
    // higher than the absolute maximum of the new product set, the old filter
    // is invalid. We should reset it automatically for a better user experience.
    if (isDirty && reduxMinPrice !== null && reduxMinPrice > ABSOLUTE_MAX) {
      dispatch(setPriceRange({ minPrice: null, maxPrice: null }));
      // The effect will re-run when Redux updates, and the `else` block below
      // will then correctly set the visual slider to the new full range.
      return;
    }
    // --- END EDGE CASE HANDLER ---

    // --- STANDARD SYNCHRONIZATION LOGIC ---
    // This runs if there's no filter active or if the active filter is still valid.
    const displayMin = reduxMinPrice ?? ABSOLUTE_MIN;
    const displayMax = reduxMaxPrice ?? ABSOLUTE_MAX;

    // Clamp the values to ensure they are within the *current* possible range.
    // This handles cases where the max value has shrunk due to other filters.
    const clampedMax = Math.min(displayMax, ABSOLUTE_MAX);
    const clampedMin = Math.min(displayMin, clampedMax); // Ensure min is not > max

    // Update the local state for the UI slider. This does NOT dispatch.
    setValues([clampedMin, clampedMax]);
  }, [
    reduxMinPrice,
    reduxMaxPrice,
    isCollectionLoading,
    ABSOLUTE_MAX,
    isDirty,
    dispatch,
  ]);

  // --- Event Handlers (WRITE to Redux) ---
  const handleFinalChange = (newValues: number[]) => {
    const [newMin, newMax] = newValues;
    dispatch(setPriceRange({ minPrice: newMin, maxPrice: newMax }));
  };

  const handleReset = () => {
    dispatch(setPriceRange({ minPrice: null, maxPrice: null }));
  };

  const isComponentLoading = isCollectionLoading || !areTranslationsReady;

  return (
    <div className="filter-widget">
      <h3 className="filter-widget__title">
        {isComponentLoading ? (
          <Skeleton width={100} />
        ) : (
          t("productsArchive.sidebar.priceTitle")
        )}
      </h3>

      <div className="price-filter">
        <div className="price-filter__slider-wrapper">
          <Range
            values={values}
            step={STEP}
            min={ABSOLUTE_MIN}
            max={ABSOLUTE_MAX}
            disabled={isComponentLoading}
            onChange={(newValues) => setValues(newValues as [number, number])}
            onFinalChange={handleFinalChange}
            renderTrack={({ props, children }) => (
              <div
                {...props}
                className={`price-filter__track ${
                  isComponentLoading ? "is-disabled" : ""
                }`}
                style={{
                  ...props.style,
                  background: getTrackBackground({
                    values,
                    colors: [
                      "var(--color-border-light)",
                      "var(--color-primary)",
                      "var(--color-border-light)",
                    ],
                    min: ABSOLUTE_MIN,
                    max: ABSOLUTE_MAX,
                  }),
                }}
              >
                {children}
              </div>
            )}
            renderThumb={({ props }) => (
              <div
                {...props}
                className={`price-filter__thumb ${
                  isComponentLoading ? "is-disabled" : ""
                }`}
              ></div>
            )}
          />
        </div>

        <div className="price-filter__inputs">
          {isComponentLoading ? (
            <Skeleton width={150} />
          ) : (
            <>
              <span>
                {t("productsArchive.sidebar.priceLabel")}:{" "}
                {`${currencySymbol}${values[0]} — ${currencySymbol}${values[1]}`}
              </span>
              {isDirty && (
                <button
                  onClick={handleReset}
                  className="price-filter__reset-btn"
                >
                  {t("productsArchive.sidebar.priceResetLabel")}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PriceFilter;
